"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Title, Description } from "@/components/Common/Typography";
import { cn } from "@/utils/cn";
import styles from "../export.module.css";

export interface ProductionSectionProps {
  data?: {
    subtitle?: string;
    title: string;
    description: string;
    images: string[];
  };
}

export default function ProductionSection({ data }: ProductionSectionProps) {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("IntersectionObserver" in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  if (!data || !data.title) return null;

  const renderProductionText = (text: string) => {
    if (!text) return null;
    const sentences = text.split(/(?<=\.)\s+/);
    return sentences.map((sentence, idx) => {
      let formatted = sentence;
      const replacements = [
        "300 ha",
        "300 hectares",
        "100 hộ nông dân",
        "100 coffee farming households",
        "3 nhà máy",
        "3 green coffee processing and production factories",
        "3 green coffee processing & production factories",
      ];
      replacements.forEach((term) => {
        if (formatted.includes(term)) {
          formatted = formatted.replace(term, `<strong>${term}</strong>`);
        }
      });
      return (
        <Description
          key={idx}
          as="p"
          size="medium"
          style={{ marginBottom: "14px", lineHeight: 1.8 }}
        >
          <span dangerouslySetInnerHTML={{ __html: formatted }} />
        </Description>
      );
    });
  };

  return (
    <section ref={sectionRef} className={styles.productionSection}>
      <div className={styles.container}>
        <div className={styles.sectionHeader}>
          <Title
            as="h2"
            size="medium"
            align="center"
            className={styles.sectionTitle}
          >
            {data.title}
          </Title>
        </div>

        <div className={styles.productionGrid}>
          <div className={styles.productionLeft}>
            <div className={styles.productionText}>
              {renderProductionText(data.description)}
            </div>
          </div>

          <div className={styles.productionRight}>
            <div className={cn(styles.showcaseWrapper, isInView && styles.inView)}>
              {/* Hình chính (Hình đầu tiên) */}
              {data.images?.[0] && (
                <div className={styles.mainImageCol}>
                  <Image
                    src={data.images[0]}
                    alt={data.title || "Viet Thien Coffee Processing Farm"}
                    fill
                    sizes="(max-width: 768px) 100vw, 40vw"
                    className={styles.showcaseImage}
                  />
                </div>
              )}

              {/* Hình deco phía trên bên phải (Lớn hơn hình bên trái phía dưới) */}
              {(data.images?.[2] || data.images?.[1]) && (
                <div className={styles.decoImageRightTop}>
                  <Image
                    src={data.images[2] || data.images[1]}
                    alt="Viet Thien Modern Coffee Production"
                    fill
                    sizes="(max-width: 768px) 50vw, 20vw"
                    className={styles.showcaseImage}
                  />
                </div>
              )}

              {/* Hình deco phía dưới bên trái (Nhỏ hơn hình bên phải phía trên) */}
              {data.images?.[2] && data.images?.[1] && (
                <div className={styles.decoImageLeftBottom}>
                  <Image
                    src={data.images[1]}
                    alt="Modern green coffee production line"
                    fill
                    sizes="(max-width: 768px) 40vw, 15vw"
                    className={styles.showcaseImage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
